import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import kebabCase from 'lodash/kebabCase';
import {
  transparent,
} from '../../utils/ColorUtil';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Slide from '@mui/material/Slide';
import VibeButton from '../VibeButton/VibeButton';
import VibeIcon from '../VibeIcon/VibeIcon';
import viAlertCircle from '../../icons/viAlertCircle';
import viCloseCircle from '../../icons/viCloseCircle';
import viArchive from '../../icons/viArchive';
import * as color from '../../sass/color.scss';
import './VibeModal.scss';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function usePrevious(value) {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
}

/**
 * Get Default Button Styles
 */
function getDefaultButtonStyle() {
  return {
    flexGrow: 1,
    fontSize: 16,
    fontWeight: 'normal',
    borderRadius: 4,
  };
}

function VibeModal({
  className = '',
  style = {},
  width = 'auto',
  height = 'auto',
  fullWidth = false,
  maxWidth = 'sm',
  type = 'confirm',
  title = null,
  text = null,
  testId = null,
  confirmProps = {
    text: 'Confirm',
    style: {},
    color: 'primary',
    link: null,
    disabled: false,
  },
  cancelProps = {
    text: 'Cancel',
    style: {},
    disabled: false,
  },
  btnText = 'Close',
  // array of action buttons to display
  // actions = [],
  show = false,
  hideIcon = false,
  children = null,
  onConfirm = () => {},
  onOpen = () => {},
  onClose = () => {},
}) {
  const prevShow = usePrevious(show);
  useEffect(() => {
    if (show && !prevShow) {
      // modal has been opened
      onOpen();
    }
  }, [show]);

  const handleClose = () => {
    onClose();
  };

  /**
   * Get the icon used for the modal (confirm, error)
   */
  const getModalIcon = () => {
    switch (type) {
      case 'error':
        return viCloseCircle;

      case 'confirm':
      default: {
        // if the title contains 'archive', change the color to red
        if (title && title.toLowerCase().includes('archive')) {
          return viArchive;
        }

        return viAlertCircle;
      }
    }
  };

  /**
   * Get the color used for the modal (confirm, error)
   */
  const getModalColor = () => {
    switch (type) {
      case 'error':
        return color.red;

      case 'confirm':
      default: {
        // if the title contains 'archive', change the color to red
        if (title && title.toLowerCase().includes('archive')) {
          return color.red;
        }

        return color.yellow;
      }
    }
  };

  /**
   * Get the Modal Test ID
   */
  const getModalTestId = () => {
    let useTestId;

    if (testId) {
      useTestId = testId;
    } else if (title) {
      useTestId = kebabCase(title);
    } else {
      useTestId = 'unknown';
    }

    return `modal-${useTestId}`;
  }

  return (
    <Dialog
      className={classNames('VibeModal', className)}
      style={style}
      open={show}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      slots={{
        transition: Transition,
      }}
      data-testid={getModalTestId()}
      // keepMounted
      /**
       * force modal button to be clicked
       * uncomment to allow closing by clicking outside of the modal
       */
      // onClose={onClose}
    >
      {!hideIcon && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: 48,
            height: 48,
            margin: 24,
            marginBottom: 0,
            backgroundColor: transparent(getModalColor(), 10),
            borderRadius: 24,
          }}
        >
          <VibeIcon
            icon={getModalIcon()}
            color={getModalColor()}
            size={24}
          />
        </div>
      )}

      {title && (
        <DialogTitle
          sx={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '18px',
          }}
        >
          {title}
        </DialogTitle>
      )}

      <DialogContent
        sx={{
          color: color.gray500,
          width,
          height,
          paddingBottom: '32px',
          fontSize: '14px',
          lineHeight: '20px',
        }}
      >
        {text || children}
      </DialogContent>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: 24,
          paddingTop: 0,
        }}
      >
        {type === 'error' && (
          <VibeButton
            variant="outlined"
            color="gray"
            text={btnText}
            height={44}
            style={getDefaultButtonStyle()}
            onClick={handleClose}
          />
        )}

        {type === 'confirm' && (
          <VibeButton
            variant="outlined"
            color={get(cancelProps, 'color') || 'gray'}
            text={cancelProps.text || 'Cancel'}
            height={44}
            style={{
              ...getDefaultButtonStyle(),
              ...cancelProps.style,
            }}
            disabled={cancelProps.disabled}
            onClick={!cancelProps.disabled
              ? handleClose
              : null}
          />
        )}

        {type === 'confirm' && (
          <VibeButton
            variant="contained"
            color={get(confirmProps, 'color') || 'primary'}
            text={confirmProps.text || 'Confirm'}
            height={44}
            style={{
              ...getDefaultButtonStyle(),
              ...confirmProps.style,
            }}
            loadingEvent="onConfirmLoading"
            link={confirmProps.link}
            disabled={confirmProps.disabled}
            onClick={!confirmProps.disabled
              ? onConfirm
              : null}
          />
        )}

        {type === 'success' && (
          <VibeButton
            variant="outlined"
            color="success"
            text={btnText}
            height={44}
            style={getDefaultButtonStyle()}
            onClick={handleClose}
          />
        )}

        {type === 'custom' && (
          <VibeButton
            variant="outlined"
            color="gray"
            text="Close"
            height={44}
            style={getDefaultButtonStyle()}
            onClick={handleClose}
          />
        )}

        {/* {actions.map((button, index) => (
          <VibeButton
            key={`action-button-${index}`}
            variant={button.variant}
            color={button.color}
            text={button.text}
            height={44}
            style={getDefaultButtonStyle()}
            loadingEvent={button.loadingEvent}
            onClick={button.onClick}
          />
        ))} */}
      </div>
    </Dialog>
  );
}

VibeModal.propTypes = {
  className: PropTypes.string,
  style: PropTypes.oneOfType([
    PropTypes.object,
  ]),
  fullWidth: PropTypes.bool,
  maxWidth: PropTypes.oneOf([
    'xs',
    'sm',
    'md',
    'lg',
    'xl',
  ]),
  width: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  height: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  type: PropTypes.oneOf([
    'error',
    'confirm',
    'success',
    'custom',
  ]),
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
  ]),
  text: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
  ]),
  confirmProps: PropTypes.shape({
    text: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.string,
    ]),
    style: PropTypes.oneOfType([
      PropTypes.object,
    ]),
    color: PropTypes.string,
    disabled: PropTypes.bool,
  }),
  cancelProps: PropTypes.shape({
    text: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.string,
    ]),
    style: PropTypes.oneOfType([
      PropTypes.object,
    ]),
    color: PropTypes.string,
    disabled: PropTypes.bool,
  }),
  btnText: PropTypes.string,
  show: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  onConfirm: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
};

export default VibeModal;
